/**
 * Maps a GetArtifactDetail `ArtifactDetail` to the `ArtifactDetailView` view
 * model.
 *
 * artifactDetails is a polymorphic document (shape per artifactType in the DAO).
 * Rather than switch on type, it is decomposed by the known DAO keys and only
 * the parts that are present are surfaced:
 *   - scalar fields                -> detailFields
 *   - latestSummary                -> bankSummary
 *   - latestProcessorResponse      -> processorResponse
 *   - responses[] (ResponseEntry)  -> responses (rawResponse as a JSON view)
 *   - response (INBOUND_OSL, free)  -> oslResponse (pretty JSON)
 * The direction/system waterfall (directions) is mapped in when that section is built.
 */
import { ArtifactDetail } from './artifactDetailApi';
import { ArtifactDetailView } from './artifactDetailTypes';
import { KeyValue, ExecutionRefRow, ExecutionRefGroup } from '../paymentDetail/paymentDetailTypes';
import {
  bottleneckLabel, formatMs, fullTimestamp, titleCase, toDirection, toFlowState,
} from '../paymentDetail/paymentDetailFormat';
import { toFlow } from '../paymentDetail/flowGraphMapper';
import { present, dash, prettyJson, toDetailFields, kvPresent, toResponses } from '../paymentDetail/detailsDocument';

/** Top-level ArtifactDetail scalar fields, mapped explicitly in display order; "-" when empty. */
const toIdentifiers = (detail: ArtifactDetail): KeyValue[] => [
  { key: 'artifactType', value: dash(detail.artifactType) },
  { key: 'systemsInvolved', value: (detail.systemsInvolved ?? []).join(', ') || '-' },
  { key: 'createdAt', value: detail.createdAt ? fullTimestamp(detail.createdAt) : '-' },
  { key: 'updatedAt', value: detail.updatedAt ? fullTimestamp(detail.updatedAt) : '-' },
];

/** Direction display order: Outbound first, Inbound next, anything else last. */
const DIRECTION_ORDER = ['OUTBOUND', 'INBOUND'];
const directionRank = (direction: string): number => {
  const index = DIRECTION_ORDER.indexOf(direction);
  return index === -1 ? DIRECTION_ORDER.length : index;
};

/** Execution references grouped by direction (Outbound first). */
const toExecutionGroups = (detail: ArtifactDetail): ExecutionRefGroup[] => {
  const groups = new Map<string, ExecutionRefRow[]>();
  (detail.executionRefs ?? []).forEach((ref, index) => {
    const direction = present(ref.direction)?.toUpperCase() ?? 'UNKNOWN';
    const rows = groups.get(direction) ?? [];
    rows.push({
      id: `${ref.executionId}-${index}`,
      executionId: dash(ref.executionId),
      executionType: dash(ref.executionType),
      system: dash(ref.system),
    });
    groups.set(direction, rows);
  });
  return Array.from(groups.entries())
    .sort(([a], [b]) => directionRank(a) - directionRank(b))
    .map(([direction, rows]) => ({ direction, rows }));
};

/* ------------------------------------------------------------------ */
/* Entry point                                                         */
/* ------------------------------------------------------------------ */

export const mapArtifactDetail = (detail: ArtifactDetail): ArtifactDetailView => {
  const ad = detail.artifactDetails ?? {};
  const overallState = toFlowState(detail.beaconArtifactState);
  const currentDirection = toDirection(detail.currentDirection);
  const latestSummary = ad.latestSummary as Record<string, unknown> | undefined;
  const latestProcessorResponse = ad.latestProcessorResponse as Record<string, unknown> | undefined;

  return {
    id: detail.id,
    statusLabel: `${detail.beaconArtifactStatus} · ${detail.beaconArtifactState}`,
    statusType: overallState === 'SUCCESS' ? 'success' : overallState === 'FAILED' ? 'error' : 'in-progress',
    locationBadge: `Currently at ${detail.currentSystem} · ${titleCase(currentDirection)}`,
    identifiers: toIdentifiers(detail),
    detailFields: toDetailFields(ad),
    bankSummary: kvPresent(latestSummary, ['status', 'detailedStatusCode', 'detailedStatusCodeCategory']),
    processorResponse: kvPresent(latestProcessorResponse, ['statusCode', 'detailedStatusCode', 'detailedMessage']),
    responses: toResponses(ad),
    oslResponse: prettyJson(ad.response),
    executions: toExecutionGroups(detail),
    correlationTags: detail.correlationTags ?? [],
    // No responseDetailsById on this operation, so inbound gaps stay unset —
    // an artifact has no bank response receipts to measure them against.
    flow: toFlow(detail.graphs ?? [], detail.totalLatencyMs),
    bottleneckLabel: bottleneckLabel(detail.bottleneck),
    totalLatency: formatMs(detail.totalLatencyMs),
  };
};
